import { paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import { t } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';

import NoDataAlert from '~components/Common/NoDataAlert';
import { ComplianceWarningDialog } from '~components/CompliancePanel/ComplianceWarningDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { FilterMenuItemLoadingReason } from '~components/Filters/FilterMenuItem';
import useAssignDriverMenu from '~components/Job/AssignJobToView/hooks/useAssignDriverMenu';
import {
  LoadingSpinner,
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import VendorTrucksAssignedSummary from '~components/TruckCapacity/VendorTrucksAssignedSummary';
import { JobAssignmentType } from '~constants/enums';
import { AccountCapacityTypeahead } from '~hooks/useAccount/models';
import useComplianceCheck from '~hooks/useComplianceCheck/useComplianceCheck';

import Search from './bulk-assign-multiple-jobs-dialog/Search';
import InfiniteScrollLoadingIndicator from './InfiniteScrollLoadingIndicator';

const DIALOG_CONTENT_INNER_GUTTER_SIZE = 1.5;

interface BulkAssignJobsDialogProps {
  accountId: string | null;
  companyId: string;
  filterDate?: string;
  isBusy?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (complianceOverride?: boolean) => void;
  onSelectedAccountChange: (account?: AccountCapacityTypeahead) => void;
}

export function BulkAssignJobsDialog({
  accountId,
  companyId,
  filterDate,
  isBusy = false,
  isOpen,
  onClose,
  onConfirm,
  onSelectedAccountChange,
}: BulkAssignJobsDialogProps) {
  const { isLoading: isChecking, result, check, reset } = useComplianceCheck();
  const [showWarning, setShowWarning] = useState(false);

  const handleAssignClick = useCallback(async () => {
    if (!accountId) return;

    const checkResult = await check(companyId, accountId);
    if (checkResult.hasIssues) {
      setShowWarning(true);
    } else {
      onConfirm();
    }
  }, [accountId, companyId, check, onConfirm]);

  const handleOverride = useCallback(() => {
    setShowWarning(false);
    reset();
    onConfirm(true);
  }, [onConfirm, reset]);

  const handleCancelWarning = useCallback(() => {
    setShowWarning(false);
    reset();
  }, [reset]);

  const handleClose = useCallback(() => {
    setShowWarning(false);
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        sx={{ [`.${paperClasses.root}`]: { height: '720px', maxWidth: '649px' } }}
      >
        <DialogHeader onClose={handleClose}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <OverflowAwareText sx={{ fontSize: '16px', fontWeight: 600 }}>
              {t('dispatch.job.bulk_assign_jobs_dialog_title')}
            </OverflowAwareText>

            <OverflowAwareText>
              {t('dispatch.job.bulk_assign_jobs_dialog_text')}
            </OverflowAwareText>
          </Box>
        </DialogHeader>

        <DialogContent
          sx={{
            '& > div': {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              pb: 0,
              pt: 1.5,
              px: 0,
            },
          }}
        >
          <VendorList
            accountId={accountId}
            companyId={companyId}
            filterDate={filterDate}
            onSelectedAccountChange={onSelectedAccountChange}
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isBusy || isChecking}
            onClick={handleClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={!accountId}
            loading={isBusy || isChecking}
            onClick={handleAssignClick}
          >
            {t('actions.assign')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>

      {result && (
        <ComplianceWarningDialog
          isOpen={showWarning}
          result={result}
          isSubmitting={isBusy}
          onCancel={handleCancelWarning}
          onOverride={handleOverride}
        />
      )}
    </>
  );
}

type VendorListProps = Pick<
  BulkAssignJobsDialogProps,
  'accountId' | 'companyId' | 'filterDate' | 'onSelectedAccountChange'
>;

function VendorList({
  accountId,
  companyId,
  filterDate,
  onSelectedAccountChange,
}: VendorListProps) {
  const {
    closeMenu,
    data,
    fetchMore,
    hasMore,
    isFetchedAfterMount,
    loadingReason,
    openMenu,
    searchBarRef,
    setSearchFieldValue,
  } = useAssignDriverMenu(companyId, JobAssignmentType.VENDOR, filterDate);
  const vendors = data.flatMap(({ entity }) =>
    entity instanceof AccountCapacityTypeahead ? [entity] : [],
  );
  const isSearching = loadingReason === FilterMenuItemLoadingReason.SEARCH_VALUE;
  const hasEmptyResults = isFetchedAfterMount && !loadingReason && !vendors.length;

  useEffect(() => {
    openMenu();

    return () => {
      closeMenu();
    };
  }, [openMenu, closeMenu]);

  return (
    <>
      <Box sx={{ display: 'flex', flex: 0, flexDirection: 'column', gap: 1.5 }}>
        <Search
          ref={searchBarRef}
          assigneeType={JobAssignmentType.VENDOR}
          onChange={setSearchFieldValue}
          sx={{ px: DIALOG_CONTENT_INNER_GUTTER_SIZE }}
        />

        <Divider />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: DIALOG_CONTENT_INNER_GUTTER_SIZE,
          py: DIALOG_CONTENT_INNER_GUTTER_SIZE,
        }}
      >
        <LoadingSpinner isVisible={isSearching} />

        {hasEmptyResults && (
          <NoDataAlert
            message={t('dispatch.order.no_assignees_for_bulk_assign', {
              entity: t('form_fields.vendor').toLocaleLowerCase(),
            })}
          />
        )}

        {!isSearching &&
          vendors.map((vendor, index) => (
            <React.Fragment key={vendor.id}>
              <VendorRow
                isSelected={vendor.id === accountId}
                onToggle={() =>
                  onSelectedAccountChange(vendor.id === accountId ? undefined : vendor)
                }
                vendor={vendor}
              />

              {index === vendors.length - 1 && hasMore && (
                <InfiniteScrollLoadingIndicator
                  isLoading={
                    loadingReason === FilterMenuItemLoadingReason.INFINITE_SCROLL
                  }
                  loadingIndicatorSize={30}
                  onFetchMore={fetchMore}
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    height: '50px',
                    justifyContent: 'center',
                  }}
                />
              )}
            </React.Fragment>
          ))}
      </Box>
    </>
  );
}

interface VendorRowProps {
  isSelected: boolean;
  onToggle: () => void;
  vendor: AccountCapacityTypeahead;
}

function VendorRow({ isSelected, onToggle, vendor }: VendorRowProps) {
  const theme = useTheme();

  return (
    <Box
      component="label"
      sx={{
        alignItems: 'flex-start',
        borderRadius: theme.brandV2.borderRadius,
        display: 'flex',
        gap: 1,
        p: 1,
        '&:hover': { bgcolor: theme.brandV2.colors.treadGray8, cursor: 'pointer' },
      }}
      data-ui={`page-dispatch__bulk-assign-jobs__vendor-${vendor.id}`}
    >
      <Checkbox
        checked={isSelected}
        onChange={onToggle}
        size="small"
        sx={{
          '&.MuiCheckbox-root': { color: theme.brandV2.colors.treadGray6, p: 0 },
          '&.Mui-checked': { color: theme.brandV2.colors.treadOrange },
        }}
        data-ui="page-dispatch__bulk-assign-jobs__vendor-checkbox"
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <VendorTrucksAssignedSummary vendor={vendor} />
      </Box>
    </Box>
  );
}
